// The right-click menus of the webtop and its tiles: the webtop menu (webtopMenu), a tile's menu (by kind; a widget
// adds its own items), the menu for several selected tiles, More, and the Size submenu every tile has.
import { WIDGETS } from '../widgets/index.js';
import { EDIT } from '../widgets/kit.js';
import { arrangeMenu } from './arrange.js';
import { backgroundMenu } from './background.js';
import { toggleAdd } from './add.js';
import { freeOnLarge, pageX, pageY, pinDrawn, sizeKey, sizeLabel, sizesFor, sizesPerScreen } from './geometry.js';
import { groupTiles, openGroup, renameGroup, ungroup } from './groups.js';
import { arrange, arranging, lastPointerType } from './input.js';
import { lockNow, securityMenu, showUnlock } from './lock.js';
import { showMenu, submenu } from './menu.js';
import { favoriteMenuItem, homeMenuItem } from './notch.js';
import { render, selectedShortcuts, setSelection } from './render.js';
import { setTileSize } from './resize.js';
import { duplicateTiles, openDialog, removeShortcuts } from './shortcuts.js';
import { loginAvailable, showLogin, signedIn, storage, webtops } from './applets.js';
import { API, byId, storageOf, $webtop, webtop, selected } from './state.js';
import { GROUPABLE, isGroup, openShortcut } from './tiles.js';
import { api, toast } from './ui.js';
import { LAYOUT_BANDS, moveTiles } from '/model.js';

// Edit, Duplicate and Delete are quick actions (icon buttons at the top of the menu, client/lib/menu.js) on every tile menu.
const duplicateItem = (list, label = 'Duplicate') => ({ label, quick: 'copy', roles: EDIT, action: () => duplicateTiles(list) });
const deleteItem = (list, label = 'Delete') => ({
  label, quick: 'trash', cls: 'danger', roles: EDIT, action: () => removeShortcuts(list),
});

// Restyle: these tiles lose every position and size they have: what each screen size saved, their own size for their
// kind's default, and their own spot for the first free one on a large screen. Only with the Viewport switch on, where
// those are the user's per screen. No other tile moves.
async function restyle(list) {
  const before = list.map((s) => ({ s, size: s.size, x: s.x, y: s.y, layouts: s.layouts }));
  const updates = list.map((s) => ({ id: s.id, clear: LAYOUT_BANDS }));
  const pins = pinDrawn(list); // what is only drawn where it is, while these still count on this screen
  moveTiles(webtop.shortcuts, updates);
  for (const s of list) s.size = sizesFor(s)[0];
  for (const s of list) {
    Object.assign(s, freeOnLarge(s));
    updates.push({ id: s.id, x: s.x, y: s.y });
  }
  render();
  try {
    await api('PATCH', `${API}/shortcuts`, [...pins, ...updates]);
    for (const s of list) await api('PATCH', `${API}/shortcuts/${s.id}`, { size: s.size });
    toast(list.length === 1 ? 'Tile restyled' : `${list.length} tiles restyled`);
  } catch (e) {
    for (const { s, layouts, ...was } of before) { Object.assign(s, was); if (layouts) s.layouts = layouts; }
    render();
    toast(e);
  }
}
const restyleItem = (list, label = 'Restyle') => (sizesPerScreen() ? [{
  label, roles: EDIT, action: () => restyle(list), title: 'Lose every position and size, on every screen size',
}] : []);

function tileMenu(s) {
  if (isGroup(s)) {
    return [
      { label: s.title || 'Group', cls: 'head' },
      { label: 'Open', action: () => openGroup(s) },
      { label: 'Edit', quick: 'edit', roles: EDIT, action: () => renameGroup(s) },
      { label: 'Size', sub: true, roles: EDIT, action: () => sizeMenu(s) },
      ...restyleItem([s]),
      { label: 'Ungroup', roles: EDIT, action: () => ungroup(s) },
      duplicateItem([s]),
      deleteItem([s]),
    ];
  }
  const widget = WIDGETS[s.kind];
  if (widget) {
    return [
      { label: widget.labelOf?.(s) ?? widget.label, cls: 'head' },
      ...widget.menu(s).filter(Boolean), // items a widget leaves out are false
      { label: 'Size', sub: true, roles: EDIT, action: () => sizeMenu(s) },
      ...restyleItem([s]),
      duplicateItem([s]),
      deleteItem([s]),
    ];
  }
  return [
    { label: 'Open', action: () => openShortcut(s) },
    { label: 'Edit', quick: 'edit', roles: EDIT, action: () => openDialog(s) },
    { label: 'Size', sub: true, roles: EDIT, action: () => sizeMenu(s) },
    ...restyleItem([s]),
    duplicateItem([s]),
    deleteItem([s]),
  ];
}

function multiMenu(list) {
  const groupable = list.every((s) => GROUPABLE.includes(s.kind));
  return [
    { label: `${list.length} Selected`, cls: 'head' },
    ...(groupable ? [{ label: 'Group', roles: EDIT, action: () => groupTiles(list) }] : []),
    ...restyleItem(list, `Restyle ${list.length}`),
    duplicateItem(list, `Duplicate ${list.length}`),
    deleteItem(list, `Delete ${list.length}`),
  ];
}

function webtopMenu(x, y) {
  const cx = pageX(x), cy = pageY(y); // the click, in unscaled px on the (scrolled) webtop
  return [
    // One plus in the quick bar, for everyone, opens the Add section for this spot, like the notch's: an editor
    // gets Webtop, Shortcut and every widget, a viewer (who may still create a webtop) Webtop alone.
    { label: 'Add', quick: 'plus', action: () => toggleAdd(true, { x: cx, y: cy }) },
    { label: 'Background', sub: true, roles: EDIT, action: backgroundMenu },
    { label: 'Arrange', sub: true, roles: EDIT, action: arrangeMenu },
    // A finger or pen moves tiles only while arranging (client/lib/input.js); a finger also starts it with a long press.
    ...(lastPointerType !== 'mouse' || arranging
      ? [{ label: arranging ? 'Done Moving' : 'Move Tiles', roles: EDIT, action: () => arrange(!arranging) }] : []),
    { label: 'More', sub: true, action: moreMenu },
    favoriteMenuItem(), // per browser, so for everyone, like Home
    ...homeMenuItem(),
    // Deleting it and its security are for whoever unlocked it (on a webtop with neither password nor owner, anyone).
    ...(webtop.unlocked ? [{ label: 'Delete Webtop', quick: 'trash', cls: 'danger', action: () => webtops((w) => w.deleteWebtop()) }] : []),
    'sep',
    // Only the server protects a webtop: one stored in this browser is this browser's alone.
    ...(webtop.unlocked && storageOf() === 'remote' ? [{ label: 'Security', sub: true, action: securityMenu }] : []),
    ...(webtop.locked && webtop.unlocked ? [{ label: 'Lock Now', action: lockNow }] : []),
    ...(webtop.locked && !webtop.unlocked ? [{ label: 'Unlock', action: () => showUnlock() }] : []),
    ...(webtop.owned && !webtop.unlocked && !signedIn() && loginAvailable() ? [{ label: 'Sign In', action: showLogin }] : []),
  ];
}

// More › Storage: the storage applet's rows for the open webtop under a heading, drawn again with fresh state.
function storageMenu() {
  storage().then((s) => submenu(() => [{ label: 'Storage', cls: 'head' }, ...s.storageItems()])).catch(toast);
}
// Submenu for whole-webtop operations: rename, Storage and export / import (Delete Webtop is a quick action on the
// webtop menu). Storage, for whoever unlocked the webtop, chooses where it is stored (the storage applet's rows,
// applets/storage/src/moves.js). Import takes a
// webtop export or a browser's bookmarks file; Export asks which of the two to write.
function moreMenu() {
  submenu([
    { label: webtop.title, cls: 'head' },
    { label: 'Rename', roles: EDIT, action: () => webtops((w) => w.rename()) },
    ...(webtop.unlocked ? [{ label: 'Storage', sub: true, action: storageMenu }] : []),
    { label: 'Export', sub: true, action: exportMenu },
    { label: 'Import', roles: EDIT, action: () => webtops((w) => w.importFile()) },
  ]);
}

function exportMenu() {
  submenu([
    { label: 'Export', cls: 'head' },
    { label: 'For Webtop', title: 'Everything on this webtop, as a file webtop can import again', action: () => webtops((w) => w.exportJson()) },
    { label: 'For Browser', title: 'The shortcuts as bookmarks (groups become folders), for Chrome, Firefox, Safari or Edge', action: () => webtops((w) => w.exportBookmarks()) },
  ]);
}

// Widget kinds live in widgets/ (one module and one stylesheet each, the shared parts in widgets/kit.js); this is
// the part of them that belongs to the webtop: their Size menu (the webtop menu's plus opens the Add section,
// client/lib/add.js).
// Size: the sizes this tile offers, plus the one it has if that is its own (a dragged corner), as radio items. The tile keeps its top-left corner; the layout makes room.
function sizeMenu(s) {
  submenu(() => [
    { label: 'Size', cls: 'head' },
    ...[...new Set([...sizesFor(s), sizeKey(s)])].sort().map((k) => ({
      label: sizeLabel(k), radio: true, checked: sizeKey(s) === k, stay: true, roles: EDIT, action: () => setTileSize(s, k),
    })),
  ]);
}
// Event wiring and first drawing, run once by client.js in the order the parts of the page depend on.
export function init() {
  $webtop.addEventListener('contextmenu', (e) => {
    e.preventDefault();
    if (!webtop) return; // the lock screen, or no webtop at this address: nothing to act on
    const tile = e.target.closest('.tile');
    if (tile) {
      const s = byId(tile.dataset.id);
      if (!selected.has(s.id)) setSelection([s.id]);
      const many = selectedShortcuts();
      // a function, so switches in a widget's menu (stay: true) redraw it with their new state
      return showMenu(e.clientX, e.clientY, many.length > 1 ? multiMenu(many) : () => tileMenu(s));
    }
    showMenu(e.clientX, e.clientY, webtopMenu(e.clientX, e.clientY));
  });
}
